export const carrierProvider = {
  one: "one",
  msc: "msc",
  hapagLloyd: "hapag-lloyd",
  cosco: "cosco",
} as const;

export type CarrierProvider =
  (typeof carrierProvider)[keyof typeof carrierProvider];

export const carrierProviders: CarrierProvider[] =
  Object.values(carrierProvider);

export interface CarrierAccountState {
  set: boolean;
  login: string | null;
  providedAt: string | null;
  revealedAt: string | null;
  revealedByName: string | null;
}

export interface CarrierIntegrationState {
  connected: boolean;
  expiresAt: string | null;
  revokedAt: string | null;
  // The next automatic reconnect attempt after a failed one; null when none is scheduled.
  reconnectRetryAt: string | null;
  automatedConnectAvailable: boolean;
  account: CarrierAccountState;
}

export type CarrierConnectionStatus =
  "ok" | "expired" | "revoked" | "missing" | "unconfigured" | "unavailable";

export interface CarrierConnectionResult extends CarrierIntegrationState {
  status: CarrierConnectionStatus;
}

export interface CarrierAccountReveal {
  login: string;
  password: string;
}

// The connection changes only through its own endpoints; this mirrors their latest reply.
export class CarrierIntegration {
  constructor(private readonly state: CarrierIntegrationState) {}

  // A token with an expiry (MSC) runs out while the page stays open.
  public isConnected(): boolean {
    return (
      this.state.connected &&
      (!this.state.expiresAt ||
        new Date(this.state.expiresAt).getTime() > Date.now())
    );
  }

  // Expired or rejected credentials keep the carrier reporting a connection issue until replaced or dropped.
  public isReconnectNeeded(): boolean {
    return (
      !this.isConnected() && (!!this.state.expiresAt || !!this.state.revokedAt)
    );
  }

  public getExpiresAt(): string | null {
    return this.state.expiresAt;
  }

  public getRevokedAt(): string | null {
    return this.state.revokedAt;
  }

  public getReconnectRetryAt(): string | null {
    return this.state.reconnectRetryAt;
  }

  // Whether this server runs the browser worker that logs in to the carrier for the customer.
  public isAutomatedConnectAvailable(): boolean {
    return this.state.automatedConnectAvailable;
  }

  public isAccountSet(): boolean {
    return this.state.account.set;
  }

  public getAccount(): CarrierAccountState {
    return { ...this.state.account };
  }

  public getState(): CarrierIntegrationState {
    return { ...this.state, account: { ...this.state.account } };
  }
}
